import { defineStore } from 'pinia'
import axios from '@/helpers/interceptors'
import { downloadBlob, getAccessToken, getFileNameFromHeader } from '@/helpers/utils'
import { B2bPortalRight } from '@/enums/B2bPortalRight'
import { piniaHasAnyPortalRight, piniaHasPortalRight } from '@/helpers/portalPermissions'
import type {
  TransactionModel,
  TransactionsListParams,
  StatementExportParams,
} from '@/types/cabinet'

function canExportAccountStatement(): boolean {
  return piniaHasAnyPortalRight([
    B2bPortalRight.AccountTransactionReport,
    B2bPortalRight.ExtendedAccountTransactionReport,
  ])
}

interface DateGroup {
  label: string
  transactions: TransactionModel[]
}

interface TransactionsState {
  transactions: TransactionModel[]
  transactionsLoading: boolean
  transactionsError: string | null
  transactionsTotalCount: number
  transactionsOffset: number
  transactionsDateFrom: string
  transactionsDateTo: string
  includeStorno: boolean
  activeAccountNumber: string | null
  /**
   * When true, `fetchTransactions` sends a wide from/to window (last
   * {@link FULL_HISTORY_YEARS} years) instead of the user-selected period, so an
   * account/wallet detail page shows its latest transactions regardless of when
   * they happened. The backend requires from/to, so this widens rather than
   * omits the range. Set as store STATE (not just a per-call arg) so concurrent
   * callers — notably the preloader, which builds its request body after an
   * `await` by which point the detail page has already set `activeAccountNumber`
   * — also use the wide range.
   */
  useFullHistoryRange: boolean
  /** Page size for the next `fetchTransactions` (detail pages use a small one). */
  fetchLimit: number
  currentTransaction: TransactionModel | null
  currentTransactionLoading: boolean
  statsTransactions: TransactionModel[]
  statsLoading: boolean
  statsError: string | null
  _fetchGeneration: number
  _statsFetchGeneration: number
}

const LIMIT = 10
// Page size for account/wallet detail cards, which show only a few latest rows.
const ACCOUNT_DETAIL_LIMIT = 3
const STATS_BATCH_SIZE = 1000
// Look-back window for account/wallet detail pages that want "latest across the
// whole history" — the backend returns nothing without a from/to, so we send a
// range wide enough to cover any realistic account age.
const FULL_HISTORY_YEARS = 5

function defaultDateFrom(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().split('T')[0]
}

function defaultDateTo(): string {
  return new Date().toISOString().split('T')[0]
}

function fullHistoryDateFrom(): string {
  const d = new Date()
  d.setFullYear(d.getFullYear() - FULL_HISTORY_YEARS)
  return d.toISOString().split('T')[0]
}

export const useTransactionsStore = defineStore('transactions', {
  state: (): TransactionsState => ({
    transactions: [],
    transactionsLoading: false,
    transactionsError: null,
    transactionsTotalCount: 0,
    transactionsOffset: 0,
    transactionsDateFrom: defaultDateFrom(),
    transactionsDateTo: defaultDateTo(),
    includeStorno: false,
    activeAccountNumber: null,
    useFullHistoryRange: false,
    fetchLimit: LIMIT,
    currentTransaction: null,
    currentTransactionLoading: false,
    statsTransactions: [],
    statsLoading: false,
    statsError: null,
    _fetchGeneration: 0,
    _statsFetchGeneration: 0,
  }),

  getters: {
    hasMore: (state): boolean => {
      if (!piniaHasPortalRight(B2bPortalRight.AccountTransactionView)) return false
      return state.transactions.length < state.transactionsTotalCount
    },

    groupedTransactions: (state): DateGroup[] => {
      if (!piniaHasPortalRight(B2bPortalRight.AccountTransactionView)) return []
      const groups: Record<string, TransactionModel[]> = {}
      const today = new Date().toDateString()
      const yesterday = new Date(Date.now() - 86400000).toDateString()

      for (const tx of state.transactions) {
        const txDate = new Date(tx.orderDate).toDateString()
        let label: string
        if (txDate === today) {
          label = 'Today'
        } else if (txDate === yesterday) {
          label = 'Yesterday'
        } else {
          const d = new Date(tx.orderDate)
          label = d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        }
        if (!groups[label]) groups[label] = []
        groups[label].push(tx)
      }

      return Object.entries(groups).map(([label, transactions]) => ({
        label,
        transactions,
      }))
    },
  },

  actions: {
    async fetchTransactions(reset = false) {
      if (reset) {
        this.transactions = []
        this.transactionsOffset = 0
        this.transactionsTotalCount = 0
        // Date range is initialized at store creation. In a long-running
        // session (or after sessionStorage hydration) `transactionsDateTo`
        // can fall behind the actual current day and hide today's records.
        // Re-anchor it whenever the caller asks for a fresh load.
        const today = defaultDateTo()
        if (this.transactionsDateTo < today) {
          this.transactionsDateTo = today
        }
      }

      if (!piniaHasPortalRight(B2bPortalRight.AccountTransactionView)) {
        this.transactionsLoading = false
        this.transactionsError = null
        return
      }

      const accessToken = await getAccessToken()
      if (!accessToken) return

      this._fetchGeneration++
      const myGeneration = this._fetchGeneration

      this.transactionsLoading = true
      this.transactionsError = null

      try {
        // Detail pages ask for the full history via a wide from/to window (the
        // backend returns nothing without a range); everything else uses the
        // user-selected period.
        const body: TransactionsListParams = this.useFullHistoryRange
          ? {
              from: `${fullHistoryDateFrom()}T00:00:00`,
              to: `${defaultDateTo()}T23:59:59`,
            }
          : {
              from: `${this.transactionsDateFrom}T00:00:00`,
              to: `${this.transactionsDateTo}T23:59:59`,
            }
        if (this.activeAccountNumber) {
          body.accountNumber = this.activeAccountNumber
          body.includeOrderStatusStorno = this.includeStorno
        }

        const limit = this.fetchLimit
        const { status, data } = await axios.post(
          `/api/transactions/list/${this.transactionsOffset}/${limit}`,
          body
        )

        if (myGeneration !== this._fetchGeneration) return // stale response, discard

        if (status === 200) {
          const newTxs: TransactionModel[] = data.transactions || []
          this.transactionsTotalCount = data.totalCount || 0

          // Deduplicate
          const existingIds = new Set(this.transactions.map((t) => t.id))
          const unique = newTxs.filter((t) => !existingIds.has(t.id))
          this.transactions = [...this.transactions, ...unique]
          this.transactionsOffset += limit
        }
      } catch (e: any) {
        if (myGeneration !== this._fetchGeneration) return // stale error, discard
        this.transactionsError =
          e?.response?.data?.message || 'Failed to load transactions'
        console.error('fetchTransactions', e)
      } finally {
        if (myGeneration === this._fetchGeneration) {
          this.transactionsLoading = false
        }
      }
    },

    async loadMore() {
      if (!this.hasMore || this.transactionsLoading) return
      await this.fetchTransactions(false)
    },

    async resetAndReload() {
      await this.fetchTransactions(true)
    },

    setDateRange(from: string, to: string) {
      this.transactionsDateFrom = from
      this.transactionsDateTo = to
      this.fetchTransactions(true)
    },

    setAccountFilter(accountNumber: string | null) {
      this.activeAccountNumber = accountNumber
      this.fetchTransactions(true)
    },

    /**
     * Configure the store for an account/wallet DETAIL view: scope to one
     * account, fetch across the full history, and cap at the few latest rows the
     * card shows. Set as store state (not just fetch args) so a concurrent
     * preloader request uses the same config. `accountNumber` may be null when
     * not yet resolved — set `activeAccountNumber` later once it is. Pair with
     * {@link exitAccountDetailView} on the page's unmount.
     */
    enterAccountDetailView(accountNumber: string | null = null) {
      this.useFullHistoryRange = true
      this.fetchLimit = ACCOUNT_DETAIL_LIMIT
      if (accountNumber) this.activeAccountNumber = accountNumber
    },

    /** Revert {@link enterAccountDetailView}; call on the detail page's unmount. */
    exitAccountDetailView() {
      this.activeAccountNumber = null
      this.useFullHistoryRange = false
      this.fetchLimit = LIMIT
    },

    toggleStorno() {
      this.includeStorno = !this.includeStorno
      this.fetchTransactions(true)
    },

    async fetchTransaction(referenceNumber: string) {
      this.currentTransactionLoading = true
      this.currentTransaction = null

      if (!piniaHasPortalRight(B2bPortalRight.PaymentView)) {
        this.currentTransactionLoading = false
        return
      }

      try {
        const { status, data } = await axios.get(
          `/api/transactions/${referenceNumber}`
        )
        if (status === 200 && data.transaction) {
          this.currentTransaction = data.transaction
        }
      } catch (e: any) {
        console.error('fetchTransaction', e)
      } finally {
        this.currentTransactionLoading = false
      }
    },

    async exportStatement(params: StatementExportParams) {
      if (!canExportAccountStatement()) {
        return
      }
      try {
        const { data, status, headers } = await axios.post(
          `/api/accounts/statement/${params.accountNumber || ''}`,
          {
            from: params.from,
            to: params.to,
            mimeType: params.mimeType,
            includeOrderStatusStorno: params.includeOrderStatusStorno || false,
            clientId: params.clientId || null,
          },
          { responseType: 'blob' }
        )
        if (status === 200) {
          await downloadBlob(
            data,
            getFileNameFromHeader(headers['content-disposition'])
          )
        }
      } catch (e: any) {
        console.error('exportStatement', e)
        throw e
      }
    },

    async fetchStatsTransactions(from: string, to: string) {
      if (!piniaHasPortalRight(B2bPortalRight.AccountTransactionView)) {
        this.statsTransactions = []
        this.statsLoading = false
        this.statsError = null
        return
      }

      const accessToken = await getAccessToken()
      if (!accessToken) return

      this._statsFetchGeneration++
      const myGeneration = this._statsFetchGeneration

      this.statsLoading = true
      this.statsError = null
      this.statsTransactions = []

      try {
        let offset = 0
        let allTxs: TransactionModel[] = []
        let totalCount = 0

        do {
          const body: TransactionsListParams = {
            from: `${from}T00:00:00`,
            to: `${to}T23:59:59`,
          }

          const { status, data } = await axios.post(
            `/api/transactions/list/${offset}/${STATS_BATCH_SIZE}`,
            body
          )

          if (myGeneration !== this._statsFetchGeneration) return // stale response, discard

          if (status === 200) {
            const batch: TransactionModel[] = data.transactions || []
            totalCount = data.totalCount || 0
            allTxs = [...allTxs, ...batch]
            offset += STATS_BATCH_SIZE
          } else {
            break
          }
        } while (offset < totalCount)

        if (myGeneration !== this._statsFetchGeneration) return // stale, discard
        this.statsTransactions = allTxs
      } catch (e: any) {
        if (myGeneration !== this._statsFetchGeneration) return // stale error, discard
        this.statsError =
          e?.response?.data?.message || 'Failed to load stats transactions'
        console.error('fetchStatsTransactions', e)
      } finally {
        if (myGeneration === this._statsFetchGeneration) {
          this.statsLoading = false
        }
      }
    },

    async downloadAdvice(referenceNumber: string) {
      if (!piniaHasPortalRight(B2bPortalRight.PaymentReport)) {
        return
      }
      try {
        const { status, data, headers } = await axios.get(
          `/api/transactions/check/${referenceNumber}`,
          { responseType: 'blob' }
        )
        if (status === 200) {
          await downloadBlob(
            data,
            getFileNameFromHeader(headers['content-disposition'])
          )
        }
      } catch (e: any) {
        console.error('downloadAdvice', e)
        throw e
      }
    },
  },
})
